import { Container } from "@chakra-ui/react"
import Hero from "@components/blocks/Hero"
import HeadingText from "@components/headings/HeadingText"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { EmenuPagesTranslation } from "@definitions/types"
import { readItem, readSingleton } from "@directus/sdk"
import { routing } from "@i18n/routing"
import ImageOne from "@public/images/hero/feature-image-1.jpg"
import ImageTwo from "@public/images/hero/feature-image-2.jpg"
import ImageThree from "@public/images/hero/feature-image-3.jpg"
import ImageFour from "@public/images/hero/feature-image-4.jpg"
import ImageFive from "@public/images/hero/feature-image-5.png"
import SvgFeature2 from "@public/svg/hero/analytics.svg"
import SvgFeature1 from "@public/svg/hero/android.svg"
import SvgFeature3 from "@public/svg/hero/apple.svg"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"

const DynamicAllFeatures = dynamic(
  () => import("@components/blocks/features/AllFeatures"),
  { ssr: false }
)
const DynamicAppVersion = dynamic(
  () => import("@components/blocks/AppVersion"),
  { ssr: false }
)
const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const Features: NextPage = ({
  page_data,
  settings,
  locales,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations("features")

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${page_data.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/${page_data.slug}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout>
        <Hero
          iconOne={SvgFeature1}
          iconTwo={SvgFeature2}
          iconThree={SvgFeature3}
          imageOne={ImageOne}
          imageTwo={ImageTwo}
          imageThree={ImageThree}
          imageFour={ImageFour}
          imageFive={ImageFive}
          alt={t("features")}
        />
      </HeaderLayout>
      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>{t("add_features")}</NormalHeading>
        <StylizedHeading>{t("within_minutes")}</StylizedHeading>
        <HeadingText>{t("add_features_text")}</HeadingText>
        <DynamicAllFeatures />
        <NormalHeading>{t("customer_experience")}</NormalHeading>
        <StylizedHeading>{t("functionalities")}</StylizedHeading>
        <HeadingText>{t("customer_experience_text")}</HeadingText>
        <DynamicAppVersion app_version={settings.app_version} />
      </Container>
      <DynamicFooter />
    </>
  )
}

export default Features

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 5, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })
  const settings = await directusClient.request(readSingleton("emenu_settings"))

  const [shared, features] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("features", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      page_data,
      settings,
      locales,
      messages: { ...shared, ...features },
    },
  }
}
